import { Injectable } from '@angular/core';
import {
  EvChargingJobRow,
  EvChargingJobRowDto,
  EvChargingJobTableDto,
} from '../../models/cityparking/ev-charging-jobs.model';

@Injectable({ providedIn: 'root' })
export class MapperEvChargingJobsService {
  mapTableFromDto(dto: EvChargingJobTableDto): {
    items: EvChargingJobRow[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
    runningCount: number;
  } {
    const raw = dto as EvChargingJobTableDto & Record<string, unknown>;
    const data = (dto.data ?? raw['Data'] ?? []) as EvChargingJobRowDto[];
    return {
      items: data.map((row) => this.mapRow(row)),
      total: dto.total ?? (raw['Total'] as number) ?? 0,
      page: dto.page ?? (raw['Page'] as number) ?? 1,
      pageSize: dto.pageSize ?? (raw['PageSize'] as number) ?? 20,
      totalPages: dto.totalPages ?? (raw['TotalPages'] as number) ?? 0,
      runningCount: dto.runningCount ?? (raw['RunningCount'] as number) ?? 0,
    };
  }

  mapRow(dto: EvChargingJobRowDto): EvChargingJobRow {
    const raw = dto as EvChargingJobRowDto & Record<string, unknown>;
    const jobStatus = String(dto.jobStatus ?? raw['JobStatus'] ?? '');
    const chargingEnd = this.asString(dto.chargingEnd ?? raw['ChargingEnd']);
    const explicitRunning = dto.isRunning ?? (raw['IsRunning'] as boolean | undefined);
    const isRunning =
      explicitRunning ??
      (jobStatus.toLowerCase() === 'running' ||
        (jobStatus.toLowerCase() !== 'completed' && !chargingEnd));

    return {
      id: dto.id ?? (raw['Id'] as number) ?? 0,
      jobId: String(dto.jobId ?? raw['JobId'] ?? ''),
      name: String(dto.name ?? raw['Name'] ?? ''),
      jobStatus,
      chargingStart: this.asString(dto.chargingStart ?? raw['ChargingStart']),
      chargingEnd,
      chargingAmount: this.asNumber(dto.chargingAmount ?? raw['ChargingAmount']),
      chargingKwh: this.asNumber(dto.chargingKwh ?? raw['ChargingKwh']),
      cpUserId: this.asNumber(dto.cpUserId ?? raw['CpUserId']),
      lineUid: String(dto.lineUid ?? raw['LineUid'] ?? ''),
      createDate: this.asString(dto.createDate ?? raw['CreateDate']),
      writeDate: this.asString(dto.writeDate ?? raw['WriteDate']),
      evTransactionId: this.asNumber(dto.evTransactionId ?? raw['EvTransactionId']),
      siteId: dto.siteId ?? (raw['SiteId'] as number) ?? 0,
      siteEv: String(dto.siteEv ?? raw['SiteEv'] ?? ''),
      siteName: String(dto.siteName ?? raw['SiteName'] ?? ''),
      licensePlate: String(dto.licensePlate ?? raw['LicensePlate'] ?? ''),
      spaceCode: String(dto.spaceCode ?? raw['SpaceCode'] ?? ''),
      historyUid: String(dto.historyUid ?? raw['HistoryUid'] ?? ''),
      chargerCode: String(dto.chargerCode ?? raw['ChargerCode'] ?? ''),
      enterAt: this.asString(dto.enterAt ?? raw['EnterAt']),
      exitAt: this.asString(dto.exitAt ?? raw['ExitAt']),
      chargerIsReady: (dto.chargerIsReady ?? raw['ChargerIsReady'] ?? null) as boolean | null,
      chargingOutstandingBalance: this.asNumber(
        dto.chargingOutstandingBalance ?? raw['ChargingOutstandingBalance']
      ),
      durationMinutes: this.asNumber(dto.durationMinutes ?? raw['DurationMinutes']),
      isRunning: !!isRunning,
    };
  }

  statusLabel(status: string): string {
    switch ((status || '').toLowerCase()) {
      case 'running':
        return 'กำลังชาร์จ';
      case 'completed':
        return 'จบแล้ว';
      default:
        return status || '—';
    }
  }

  private asString(value: unknown): string | null {
    if (value == null || value === '') return null;
    return String(value);
  }

  private asNumber(value: unknown): number | null {
    if (value == null || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
}
